import _ from 'lodash'

import i18n from '@/i18n/i18n'
import TarfinProQuotationDocumentService from '@/services/admin/TarfinProQuotationDocumentService'
import TarfinProQuotationService from '@/services/admin/TarfinProQuotationService'

import { build_query, errorMessage } from '@/utilities/helpers'

import { STATUS_TYPE } from '@/constants/TarfinProQuotationStatusType'

const { t } = i18n.global

export const namespaced = true

export const allowedFilters = ['status', 'retailer_id', 'quotation_date_between']

export const allowedIncludes = 'retailer,items.brands.productUnit,items.brands.brand,items.product'

export const allowedSorts = '-created_at'
export const allowedAppends = 'products'

export const state = {
  quotations: null,
  quotation: null,
  retailers: [],
  meta: null,
  collections: []
}

export const mutations = {
  SET_QUOTATIONS(state, quotations) {
    state.quotations = quotations.data
    state.meta = quotations.meta
  },
  SET_RETAILERS(state, retailers) {
    state.retailers = retailers.data
  },
  SET_QUOTATION(state, quotation) {
    state.quotation = quotation
  },
  SET_COLLECTIONS(state, collections) {
    state.collections = collections
  },
  DELETE_QUOTATION(state, quotationId) {
    state.quotations = _.filter(state.quotations, function (quotation) {
      return quotation.id !== quotationId
    })
  },
  UPDATE_QUOTATION(state, params) {
    state.quotation[params.type] = params.value
  }
}

export const getters = {
  getQuotation: state => state.quotation
}

export const actions = {
  getQuotations({ commit, dispatch, state }, query) {
    let newQuery = build_query(query, allowedFilters, allowedIncludes, allowedSorts, allowedAppends)

    return new Promise((resolve, reject) => {
      TarfinProQuotationService.getQuotations(newQuery)
        .then(response => {
          commit('SET_QUOTATIONS', response)
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message:
              t('store.shared.fetching_problem', {
                element: t('store.admin.tarfin_pro_quotation.quotation')
              }) + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getRetailersForQuotation({ commit }, regionId) {
    return new Promise(resolve => {
      TarfinProQuotationService.getRetailersForQuotation(regionId).then(response => {
        commit('SET_RETAILERS', response)
        resolve(response)
      })
    })
  },
  getQuotation({ commit, state, getters }, id) {
    return new Promise(resolve => {
      TarfinProQuotationService.getQuotation(id).then(response => {
        commit('SET_QUOTATION', response.data)
        resolve(response.data)
      })
    })
  },
  updateQuotation({ dispatch, commit }, quotation) {
    return new Promise((resolve, reject) => {
      TarfinProQuotationService.updateQuotation(quotation.id, quotation)
        .then(response => {
          commit('SET_QUOTATION', response.data)
          resolve(response.data)
        })
        .catch(err => {
          const notifications = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notifications, {
            root: true
          })
          reject(err)
        })
    })
  },
  createQuotation({ dispatch, commit, state }, quotation) {
    return new Promise((resolve, reject) => {
      TarfinProQuotationService.createQuotation(quotation)
        .then(response => {
          commit('SET_QUOTATION', response.data)
          resolve(response.data)
        })
        .catch(err => {
          reject(err)
        })
    })
  },
  deleteQuotation({ dispatch, commit, state }, params) {
    return new Promise((resolve, reject) => {
      TarfinProQuotationService.deleteQuotation(params.id)
        .then(response => {
          commit('DELETE_QUOTATION', params.id)
          const notification = {
            type: 'success',
            message: t('store.shared.deleted', {
              element: t('store.admin.tarfin_pro_quotation.quotation')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message:
              t('store.shared.delete_error', {
                element: t('store.admin.tarfin_pro_quotation.quotation')
              }) + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  createDocuments({ commit }, quotationId) {
    return new Promise((resolve, reject) => {
      TarfinProQuotationDocumentService.createDocuments(quotationId)
        .then(response => {
          commit('UPDATE_QUOTATION', { type: 'documents', value: response.data })
          resolve(response)
        })
        .catch(err => {
          reject(err)
        })
    })
  },
  sendQuotationToRetailer({ dispatch, commit }, quotation) {
    return new Promise((resolve, reject) => {
      TarfinProQuotationService.sendQuotationToRetailer(quotation)
        .then(response => {
          commit('SET_QUOTATION', response.data)
          const notification = {
            type: 'success',
            message: t('views.admin.tarfin_pro_quotations.offer_send')
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response)
        })
        .catch(err => {
          const notifications = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notifications, {
            root: true
          })
          reject(err)
        })
    })
  },
  cancelQuotation({ commit }, quotation) {
    return new Promise((resolve, reject) => {
      TarfinProQuotationService.cancelQuotation(quotation.id, quotation.rejection_reason)
        .then(response => {
          commit('UPDATE_QUOTATION', { type: 'status', value: STATUS_TYPE.CANCELLED })
          resolve(response)
        })
        .catch(err => {
          reject(err)
        })
    })
  },
  exportQuotations({ commit, dispatch }, query) {
    let newQuery = build_query(query, allowedFilters)

    return new Promise(reject => {
      TarfinProQuotationService.exportQuotations(newQuery)
        .then(() => {
          const notification = {
            type: 'info',
            message: t('store.admin.tarfin_pro_quotation.export_quotations_success')
          }
          dispatch('notification/add', notification, {
            root: true
          })
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.admin.tarfin_pro_quotation.export_quotations_error') + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  generateMaturityDays({ dispatch, commit }, query) {
    return new Promise((resolve, reject) => {
      TarfinProQuotationService.generateMaturityDays(query)
        .then(response => {
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  sendAllocationStatus({ dispatch, commit }, quotationStatusAndId) {
    return new Promise((resolve, reject) => {
      TarfinProQuotationService.sendAllocationStatus(quotationStatusAndId.id, quotationStatusAndId.status)
        .then(response => {
          commit('SET_QUOTATION', response.data)
          resolve(response)
        })
        .catch(err => {
          const notifications = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notifications, {
            root: true
          })
          reject(err)
        })
    })
  },
  approveSignedPromissory({ dispatch, commit }, quotationId) {
    return new Promise((resolve, reject) => {
      TarfinProQuotationService.approveSignedPromissory(quotationId)
        .then(response => {
          commit('SET_QUOTATION', response.data)
          const notifications = {
            type: 'success',
            message: t('store.shared.updated', {
              element: t('store.admin.document.document')
            })
          }
          dispatch('notification/add', notifications, {
            root: true
          })
          resolve(response)
        })
        .catch(err => {
          reject(err)
        })
    })
  },
  destroyPromissoryNote({ dispatch, commit }, documentId) {
    return new Promise((resolve, reject) => {
      TarfinProQuotationService.destroyPromissoryNote(documentId)
        .then(response => {
          commit('UPDATE_QUOTATION', { type: 'documents', value: response.data })
          const notifications = {
            type: 'success',
            message: t('store.shared.deleted', {
              element: t('store.admin.document.document')
            })
          }
          dispatch('notification/add', notifications, {
            root: true
          })
          resolve(response)
        })
        .catch(err => {
          reject(err)
        })
    })
  },
  getQuotationCollection({ commit }, quotationId) {
    return new Promise((resolve, reject) => {
      TarfinProQuotationService.getQuotationCollection(quotationId)
        .then(response => {
          commit('SET_COLLECTIONS', response.data)
          resolve(response)
        })
        .catch(err => {
          reject(err)
        })
    })
  },
  deleteQuotationCollection({ dispatch, commit }, params) {
    return new Promise((resolve, reject) => {
      TarfinProQuotationService.deleteQuotationCollection(params)
        .then(response => {
          commit('SET_COLLECTIONS', response.data)
          dispatch('getQuotation', params.quotationId)
          const notifications = {
            type: 'success',
            message: t('store.shared.deleted', {
              element: t('constants.promissory_note_reason_type.collection')
            })
          }
          dispatch('notification/add', notifications, {
            root: true
          })
          resolve(response)
        })
        .catch(err => {
          reject(err)
        })
    })
  },
  createQuotationCollection({ dispatch, commit }, params) {
    return new Promise((resolve, reject) => {
      TarfinProQuotationService.createQuotationCollection(params.quotationId, params.collection)
        .then(response => {
          commit('SET_COLLECTIONS', response.data)
          dispatch('getQuotation', params.quotationId)
          const notifications = {
            type: 'success',
            message: t('store.shared.save', {
              element: t('constants.promissory_note_reason_type.collection')
            })
          }
          dispatch('notification/add', notifications, {
            root: true
          })
          resolve(response)
        })
        .catch(err => {
          const notifications = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notifications, {
            root: true
          })
          reject(err)
        })
    })
  }
}
